import { forwardRef, type ComponentProps } from 'react';

import NextLink from 'next/link';

import { Box, Link, Typography } from '@mui/material';

import { IdentityRow, identityFromDocProvenance } from '@/components/identity';
import type { DocSearchResult, PublicDocId, ThreadSearchResult } from '@/lib/api';
import { formatCompactRelativeTimestamp } from '@/lib/utils';

/** Adapts Next.js links to MUI while retaining native anchor behavior. */
const LinkBehavior = forwardRef<HTMLAnchorElement, ComponentProps<typeof NextLink>>(
  function LinkBehavior(props, ref) {
    return <NextLink ref={ref} {...props} />;
  }
);

/** Opens this post with pointer or keyboard input while rendering its excerpt as plain text. */
function PostExcerpt({
  post,
  onOpen,
}: {
  post: DocSearchResult;
  onOpen: (docId: PublicDocId) => void;
}): React.JSX.Element {
  return (
    <Link
      component={LinkBehavior}
      href={`/doc/${post.doc_id}`}
      prefetch={false}
      color="inherit"
      underline="none"
      onClick={(event) => {
        // Modified clicks keep browser link behavior and leave the search open.
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        onOpen(post.doc_id);
      }}
      sx={{
        display: 'block',
        p: 1,
        borderRadius: 1,
        transition: 'background-color 0.2s',
        '&:hover, &:focus-visible': {
          backgroundColor: 'var(--mui-palette-action-selected)',
        },
        '&:focus-visible': {
          outline: '2px solid var(--mui-palette-primary-main)',
          outlineOffset: -2,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <IdentityRow
          identity={identityFromDocProvenance(post.provenance)}
          linkMode="text"
          avatarSize={24}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {formatCompactRelativeTimestamp(post.provenance.createdon)}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
        {post.excerpt}
      </Typography>
    </Link>
  );
}

/** Groups an opening preview and matching posts, each with its own navigation target. */
export function ThreadSearchResultCard({
  result,
  onOpen,
}: {
  result: ThreadSearchResult;
  onOpen: (docId: PublicDocId) => void;
}): React.JSX.Element {
  const preview = result.opening_doc ?? result.best_match;
  const matches = [result.best_match, ...result.other_matches].filter(
    (post) => post.doc_id !== preview.doc_id
  );
  return (
    <Box
      role="group"
      aria-label="Thread"
      sx={{
        p: 0.5,
        mb: 1,
        borderRadius: 1,
        backgroundColor: 'var(--mui-palette-action-hover)',
      }}
    >
      <PostExcerpt post={preview} onOpen={onOpen} />
      {matches.length > 0 && (
        <Box
          sx={{
            mt: 0.5,
            ml: 1.5,
            mr: 0.5,
            mb: 0.5,
            pl: 0.5,
            borderLeft: '2px solid var(--mui-palette-divider)',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            Matching posts
          </Typography>
          {matches.map((post) => (
            <Box key={post.doc_id} sx={{ mt: 0.5 }}>
              <PostExcerpt post={post} onOpen={onOpen} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
